import { onMounted, ref, watch } from "@vue/composition-api";
import { getTextHeight } from "../services/Canvas2DContext";
import { DefaultConfigs, IPadProConfigs } from "../services/DefaultConfigs";

function shiftYByTextHeight(previousY, labelProps) {
  const font = `${labelProps.fontWeight} ${labelProps.fontSize}px ${labelProps.fontFamily}`;
  const textHeight = getTextHeight(font, labelProps.text);

  return previousY - textHeight;
}

const SUP_OPEN_TAG = /<sup>/ig;
const SUP_CLOSE_TAG = /<\/sup>/ig;
const SUB_OPEN_TAG = /<sup>/ig;
const SUB_CLOSE_TAG = /<\/sup>/ig;
const NO_BREAK_TAG = /<\/?no-break>/ig;

export default function useChartYAxisLabel(configRef, availableHeightRef, marginRef, isIPadProRef) {
  const yAxisLabels = ref([]);

  const updateChartYAxisLabels = () => {
    if (!configRef?.value) {
      return;
    }

    let config = configRef.value?.yAxis?.label ?? {};

    const {
      fontFamily = DefaultConfigs.YAxis.fontFamily,
      fontSize = isIPadProRef?.value ? IPadProConfigs.YAxis.fontSize : DefaultConfigs.YAxis.fontSize,
      fontWeight = DefaultConfigs.YAxis.fontWeight || "",
      textColor = DefaultConfigs.YAxis.textColor,
      textAnchor = DefaultConfigs.YAxis.textAnchor,
      rotate = DefaultConfigs.YAxis.rotate,
    } = config;

    let {
      x = DefaultConfigs.YAxis.x,
      y = DefaultConfigs.YAxis.y,
    } = config;

    // --------------------------------------------------------------------------------
    // This is tricky!
    // --------------------------------------------------------------------------------
    // Due to the rotation, `x` values actually have to be assigned
    // to `y` coordinates and vice-versa
    if (marginRef.value?.top != null && availableHeightRef.value != null) {
      x = -((availableHeightRef.value + marginRef.value.top) / 2);
    }

    // The array spread is required, as the array that comes from the config
    // is altered by Vue and cannot be reversed
    const labels = [ ...(Array.isArray(config.text) ? config.text : [config.text]) ];

    yAxisLabels.value = labels.reverse().map(label => {
      let result = {
        fontFamily,
        fontSize,
        fontWeight,
        textColor,
        rotate,
        textAnchor,
        x,
        y,
        text: label,
      };

      if (typeof label === "string") {
        result = {
          ...result,
          text: label,
        };
        // TODO: adjust superscript tspan with fontSize adjustments
        // based on fontSize set for main label
        result.text = result.text.replace(SUP_OPEN_TAG, '<tspan baseline-shift="super">');
        result.text = result.text.replace(SUP_CLOSE_TAG, "</tpsan>")
        result.text = result.text.replace(SUB_OPEN_TAG, '<tspan baseline-shift="sub">');
        result.text = result.text.replace(SUB_CLOSE_TAG, "</tpsan>")
        result.text = result.text.replace(NO_BREAK_TAG, "")
      } else {
        result = {
          ...result,
          ...label,
          // we always want to use the computed coordinates
          x,
          y,
        };
      }

      y = shiftYByTextHeight(y, result);

      return result;
    });
  };

  watch([configRef, availableHeightRef, marginRef, isIPadProRef], updateChartYAxisLabels);

  onMounted(updateChartYAxisLabels);

  return { yAxisLabels };
}
